import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  useTheme as useMuiTheme,
  Backdrop,
  Alert,
  alpha,
  Autocomplete
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  Route as RouteIcon,
  Satellite as SatelliteIcon,
  DirectionsCar as VehicleIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import XLSX from 'sheetjs-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from "../utlis/axiosInstance";
import moment from 'moment';
import { useTheme } from "../utlis/useTheme";
import DeviceSearchSelect, { DeviceOption } from "../components/DeviceSearchSelect";
import styles from './SummaryReport.module.css';

interface ReportData {
  total_distance: number;
  max_speed: number;
  avg_speed: number;
  ignition_hours: number;
  idle_time: number;
  running_time: number;
  stop_time: number;
  inactive_time: string;
  charging_time: number;
  gps_tracking_time: number;
  start_odometer: number;
  end_odometer: number;
  start_position: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  end_position: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  current_status: string;
  current_battery_level: number | null;
  average_battery_level: number;
  most_common_voltage: string;
  most_common_gsm_signal: string;
  voltage_level_distribution: Record<string, number>;
  gsm_signal_distribution: Record<string, number>;
  deviceId?: string;
  deviceName?: string;
}

const formatTimeToHoursMinutes = (decimalHours: number) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours || 0} h ${minutes || 0} m`;
};

const safeNumber = (value: number | undefined | null) =>
  typeof value === 'number' && isFinite(value) ? value : 0;

const getStatusChipClass = (status: string) => {
  const statusLower = status?.toLowerCase();
  if (statusLower === 'online') return styles.success;
  if (statusLower === 'offline') return styles.error;
  return styles.default;
};

const SummaryReport: React.FC = () => {
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } = useTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [reportData, setReportData] = useState<(ReportData & { startTime: string, endTime: string })[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<DeviceOption[]>(
    id ? [{ id, name: id }] : []
  );
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0); // Today 12:00 AM
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  const [reportFetched, setReportFetched] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Helper function to extract report data from API response
  const extractReportData = (responseData: any, deviceId: string | number) => {
    if (!responseData) return null;

    const deviceIdStr = String(deviceId);
    const deviceIdNum = typeof deviceId === 'string' ? Number(deviceId) : deviceId;

    // Check if responseData is keyed by device ID (try both string and number)
    if (responseData[deviceIdStr]) {
      return responseData[deviceIdStr];
    }
    if (deviceIdNum && !isNaN(deviceIdNum) && responseData[deviceIdNum]) {
      return responseData[deviceIdNum];
    }
    if (responseData[deviceId]) {
      return responseData[deviceId];
    }

    // Check if responseData is directly date-keyed
    if (typeof responseData === 'object' && !responseData.total_distance && !responseData.running_time) {
      // Return the date-keyed object itself so we can process all dates
      return responseData;
    }
    // Otherwise, use responseData directly
    return responseData;
  };

  const handleGenerateReport = async () => {
    if (!selectedDevices.length) {
      setError("Please select at least one device.");
      return;
    }

    setLoading(true);
    setError(null);
    setReportData([]);
    setReportFetched(false);

    try {
      // Create device name mapping
      const deviceIdToName: Record<string, string> = Object.fromEntries(
        selectedDevices.map((device) => [device.id, device.name || device.id])
      );

      const selectedDeviceIds = selectedDevices.map((device) => device.id);

      // Build device_ids query string
      const deviceIdsParams = selectedDeviceIds.map(id => `device_ids=${id}`).join('&');

      // Single API call with multiple device_ids for entire date range
      // Use first device ID in path, and all device_ids in query params
      // Match mobile app time calculation: subtract 5 hours 30 minutes (UTC to IST offset)
      const fromStr = moment(fromDate)
        .startOf("day")
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");
      const toStr = moment(toDate)
        .endOf("day")
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");
      const firstDeviceId = selectedDeviceIds[0];

      const response = await axiosInstance.get(
        `/gpstracker/device/${firstDeviceId}/report?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}&report=summary&${deviceIdsParams}`
      );

      const results: (ReportData & { startTime: string, endTime: string })[] = [];
      const data = response.data;

      if (data?.status && data?.responseData) {
        const responseData = data.responseData;

        // Log response structure for debugging
        console.log("Response data structure:", Object.keys(responseData));
        console.log("Selected device IDs:", selectedDevices);

        // Process each device's data
        for (const deviceId of selectedDeviceIds) {
          const deviceReportData = extractReportData(responseData, deviceId);

          if (!deviceReportData) {
            console.log(`No data found for device ID: ${deviceId}`);
            continue;
          }

          // If deviceReportData is date-keyed, process all dates
          if (deviceReportData && typeof deviceReportData === 'object' && !deviceReportData.total_distance && !deviceReportData.running_time) {
            // Multiple dates in the response - create entries for each date
            const dateKeys = Object.keys(deviceReportData).sort();
            for (const dateKey of dateKeys) {
              const dayData = deviceReportData[dateKey];
              if (dayData) {
                const date = moment(dateKey);
                results.push({
                  ...dayData,
                  startTime: date.startOf("day").toISOString(),
                  endTime: date.endOf("day").toISOString(),
                  deviceId,
                  deviceName: deviceIdToName[deviceId] || deviceId,
                });
              }
            }
          } else if (deviceReportData) {
            // Single report data object for the entire date range
            results.push({
              ...deviceReportData,
              startTime: fromStr,
              endTime: toStr,
              deviceId,
              deviceName: deviceIdToName[deviceId] || deviceId,
            });
          }
        }
      }

      // Sort by date then by device name for stable display
      const sortedResults = results.sort((a, b) => {
        const t = moment(a.startTime).valueOf() - moment(b.startTime).valueOf();
        if (t !== 0) return t;
        return (a.deviceName || "").localeCompare(b.deviceName || "");
      });

      setReportData(sortedResults);
      if (sortedResults.length === 0) {
        setError("No data found for the selected range.");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Error generating report. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
      setReportFetched(true);
    }
  };

  const handleExportExcel = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      // Header rows: From, To, Devices, empty row, then column headers matching the on-screen table
      const headerRows = [
        ["From DateTime", moment(fromDate).format('DD:MM:YYYY')],
        ["To DateTime", moment(toDate).format('DD:MM:YYYY')],
        ["Devices", selectedDevices.map(device => device.name || device.id).join(', ')],
        [],
        [
          'Device',
          'Start Time',
          'End Time',
          'Total Distance (km)',
          'Max Speed (km/h)',
          'Average Speed (km/h)',
          'Ignition Hours',
          'Idle Time',
          'Running Time',
          'Stop Time',
          'Inactive Time',
          'GPS Tracking Time',
          'Start Odometer (km)',
          'End Odometer (km)',
          'Start Position',
          'End Position',
          'Current Status',
          'Current Battery Level'
        ]
      ];

      // Data rows for all report data
      const dataRows = reportData.map((entry) => [
        entry?.deviceName || entry?.deviceId || '-',
        moment(entry.startTime).format('DD:MM:YYYY'),
        moment(entry.endTime).format('DD:MM:YYYY'),
        safeNumber(entry?.total_distance).toFixed(2),
        safeNumber(entry?.max_speed).toFixed(2),
        safeNumber(entry?.avg_speed).toFixed(2),
        entry?.ignition_hours ?? '-',
        entry?.idle_time ?? '-',
        entry?.running_time ?? '-',
        entry?.stop_time ?? '-',
        entry?.inactive_time ?? '-',
        entry?.gps_tracking_time ?? '-',
        safeNumber(entry?.start_odometer).toFixed(2),
        safeNumber(entry?.end_odometer).toFixed(2),
        entry?.start_position?.address || (entry?.start_position ? `${entry.start_position.latitude}, ${entry.start_position.longitude}` : '-'),
        entry?.end_position?.address || (entry?.end_position ? `${entry.end_position.latitude}, ${entry.end_position.longitude}` : '-'),
        entry?.current_status ?? '-',
        entry?.current_battery_level ?? 'N/A'
      ]);

      const allRows = [...headerRows, ...dataRows];

      setDownloadProgress(50);

      const worksheet = XLSX.utils.aoa_to_sheet(allRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary Report');

      setDownloadProgress(80);

      XLSX.writeFile(workbook, `Summary_Report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);

      setDownloadProgress(100);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('Failed to export report. Please try again.');
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 800);
    }
  };

  const handleExportPDF = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(14);
      doc.text('Summary Report', 14, 16);
      doc.setFontSize(10);
      doc.text(`From: ${moment(fromDate).format('DD:MM:YYYY')}`, 14, 24);
      doc.text(`To: ${moment(toDate).format('DD:MM:YYYY')}`, 110, 24);

      const columns = [
        'Device',
        'Start Time',
        'End Time',
        'Total Distance (km)',
        'Max Speed (km/h)',
        'Average Speed (km/h)',
        'Ignition Hours',
        'Idle Time',
        'Running Time',
        'Stop Time',
        'Inactive Time',
        'GPS Tracking Time',
        'Start Odometer (km)',
        'End Odometer (km)',
        'Start Position',
        'End Position',
        'Current Status',
        'Current Battery Level'
      ];

      const rows = reportData.map(entry => [
        entry?.deviceName || entry?.deviceId || '-',
        moment(entry.startTime).format('DD:MM:YYYY'),
        moment(entry.endTime).format('DD:MM:YYYY'),
        safeNumber(entry?.total_distance).toFixed(2),
        safeNumber(entry?.max_speed).toFixed(2),
        safeNumber(entry?.avg_speed).toFixed(2),
        entry?.ignition_hours ?? '-',
        entry?.idle_time ?? '-',
        entry?.running_time ?? '-',
        entry?.stop_time ?? '-',
        entry?.inactive_time ?? '-',
        entry?.gps_tracking_time ?? '-',
        safeNumber(entry?.start_odometer).toFixed(2),
        safeNumber(entry?.end_odometer).toFixed(2),
        entry?.start_position?.address || (entry?.start_position ? `${entry.start_position.latitude}, ${entry.start_position.longitude}` : '-'),
        entry?.end_position?.address || (entry?.end_position ? `${entry.end_position.latitude}, ${entry.end_position.longitude}` : '-'),
        entry?.current_status ?? '-',
        entry?.current_battery_level ?? 'N/A'
      ]);

      setDownloadProgress(30);

      // Use autotable plugin
      // @ts-ignore
      (autoTable as any)(doc, {
        head: [columns],
        body: rows,
        startY: 30,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [230, 236, 255] },
        theme: 'grid',
        didDrawPage: (dataArg: any) => {
          // page footer
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : (pageSize as any).getHeight();
          doc.setFontSize(9);
          const pageNumber = doc.getNumberOfPages();
          doc.text(`Page ${pageNumber}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 10);
        }
      });

      setDownloadProgress(80);

      doc.save(`Summary_Report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`);

      setDownloadProgress(100);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 800);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = reportData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(reportData.length / rowsPerPage);

  return (
    <Box
      sx={{
        fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: alpha(getPrimaryColor() || "#1D2B56", 0.02),
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            padding: '1.5rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <Button
            onClick={() => navigate('/reports')}
            sx={{
              color: '#6b7280',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '0.5rem',
              minWidth: 'auto',
              '&:hover': {
                color: '#374151',
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            <ArrowBackIcon sx={{ width: '1.5rem', height: '1.5rem' }} />
          </Button>
          <Typography
            variant="h5"
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1f2937',
              margin: 0,
            }}
          >
            Summary Report
          </Typography>
        </Box>
      </Box>

      <Box sx={{ padding: '1.5rem' }}>

        {/* Error Alert */}
        {error && (
          <Box sx={{ margin: '1rem 0' }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Filter Panel */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${alpha(getPrimaryColor() || "#1D2B56", 0.2)}`,
            padding: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'nowrap',
              justifyContent: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '160px',
                maxWidth: '200px',
                flex: '0 0 auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                From Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  value={fromDate}
                  onChange={(newValue: Date | null) => newValue && setFromDate(newValue)}
                  maxDateTime={toDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: { fontSize: '0.75rem' }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '160px',
                maxWidth: '200px',
                flex: '0 0 auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                To Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  value={toDate}
                  onChange={(newValue: Date | null) => newValue && setToDate(newValue)}
                  minDateTime={fromDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: { fontSize: '0.75rem' }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '200px',
                maxWidth: '300px',
                flex: '0 0 auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                Select Devices
              </Typography>
              <DeviceSearchSelect
                label="Add Device"
                multiple
                value={selectedDevices}
                onChange={(newValue) =>
                  setSelectedDevices((newValue as DeviceOption[]) || [])
                }
                maxSelection={10}
                onMaxSelectionReached={() =>
                  setError("Maximum 10 devices can be selected.")
                }
              />
              {/* Selected Devices Summary */}
              {selectedDevices.length > 0 && (
                <Box sx={{ marginTop: '0.5rem' }}>
                  {selectedDevices.length > 50 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #90caf9',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        gap: '0.5rem',
                      }}
                    >
                      <Typography sx={{ color: '#0284F5', fontSize: '0.75rem', fontWeight: 500 }}>
                        {`${selectedDevices.length.toLocaleString()} Devices Selected`}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setSelectedDevices([])}
                        sx={{
                          minWidth: 'auto',
                          padding: '0.25rem 0.5rem',
                          color: '#0284F5',
                          fontSize: '0.7rem',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'rgba(2, 132, 245, 0.1)',
                          },
                        }}
                      >
                        Clear All
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {selectedDevices.map((device) => {
                        return (
                          <Box
                            key={device.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: '#e3f2fd',
                              border: '1px solid #90caf9',
                              borderRadius: '16px',
                              padding: '0.25rem 0.5rem',
                              gap: '0.25rem',
                            }}
                          >
                            <Typography sx={{ color: '#0284F5', fontSize: '0.75rem' }}>
                              {device.name || device.id}
                            </Typography>
                            <Button
                              size="small"
                              onClick={() =>
                                setSelectedDevices((prev) =>
                                  prev.filter((selected) => selected.id !== device.id)
                                )
                              }
                              sx={{
                                minWidth: 'auto',
                                padding: '0',
                                color: '#0284F5',
                                '&:hover': {
                                  backgroundColor: 'rgba(2, 132, 245, 0.1)',
                                },
                              }}
                            >
                              ×
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            >
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleGenerateReport}
                disabled={loading || !selectedDevices.length}
                sx={{
                  bgcolor: getPrimaryColor() || "#1D2B56",
                  color: 'white !important',
                  padding: '0.4rem 0.8rem !important',
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1) !important',
                  minWidth: '75px',
                  height: '32px',
                  '&:hover': {
                    bgcolor: getPrimaryLightColor(),
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15) !important',
                  },
                }}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : 'Generate'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                disabled={downloading || reportData.length === 0}
                sx={{
                  color: '#059669 !important',
                  borderColor: '#059669 !important',
                  padding: '0.4rem 0.8rem !important',
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  minWidth: '65px',
                  height: '32px',
                  borderWidth: '1px !important',
                  '&:hover': {
                    backgroundColor: '#059669 !important',
                    color: 'white !important',
                  },
                }}
              >
                {downloading ? <CircularProgress size={16} color="inherit" /> : 'Excel'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                disabled={reportData.length === 0}
                sx={{
                  color: '#dc2626 !important',
                  borderColor: '#dc2626 !important',
                  padding: '0.4rem 0.8rem !important',
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  minWidth: '65px',
                  height: '32px',
                  borderWidth: '1px !important',
                  '&:hover': {
                    backgroundColor: '#dc2626 !important',
                    color: 'white !important',
                  },
                }}
              >
                PDF
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Table */}
        {reportData.length > 0 ? (
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead
                  style={{
                    background: 'linear-gradient(to right, #eff6ff, #e0e7ff)',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <tr>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Device</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>End Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Distance (km)</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Max Speed (km/h)</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Average Speed (km/h)</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ignition Hours</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Idle Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Running Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stop Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Inactive Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>GPS Tracking Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Odometer (km)</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>End Odometer (km)</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Position</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>End Position</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Status</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Battery Level</th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: 'white' }}>
                  {currentData.map((data: any, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'all 0.15s ease',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eff6ff')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9fafb')}
                    >
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937', fontWeight: 600 }}>
                        {data?.deviceName || data?.deviceId || '-'}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {moment(data.startTime).format('YYYY-MM-DD HH:mm')}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {moment(data.endTime).format('YYYY-MM-DD HH:mm')}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: '#dbeafe',
                            color: '#1e40af',
                            border: '1px solid #bfdbfe',
                          }}
                        >
                          {safeNumber(data?.total_distance).toFixed(2)} km
                        </Box>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#dc2626', fontWeight: 600 }}>
                        {safeNumber(data?.max_speed).toFixed(2)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#7c3aed', fontWeight: 600 }}>
                        {safeNumber(data?.avg_speed).toFixed(2)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {(data?.ignition_hours)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {(data?.idle_time)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {(data?.running_time)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {(data?.stop_time)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {(data?.inactive_time)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {(data?.gps_tracking_time)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>
                        {safeNumber(data?.start_odometer).toFixed(2)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>
                        {safeNumber(data?.end_odometer).toFixed(2)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#64748b' }}>
                        {data?.start_position?.address || '-'}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#64748b' }}>
                        {data?.end_position?.address || '-'}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: 600,
                            gap: '4px',
                            backgroundColor: data?.current_status?.toLowerCase() === 'online' ? '#dcfce7' : data?.current_status?.toLowerCase() === 'offline' ? '#fee2e2' : '#f3f4f6',
                            color: data?.current_status?.toLowerCase() === 'online' ? '#166534' : data?.current_status?.toLowerCase() === 'offline' ? '#991b1b' : '#374151',
                            border: '1px solid',
                            borderColor: data?.current_status?.toLowerCase() === 'online' ? '#bbf7d0' : data?.current_status?.toLowerCase() === 'offline' ? '#fecaca' : '#e5e7eb',
                          }}
                        >
                          {data?.current_status}
                        </Box>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>
                        {data?.current_battery_level || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="h6" color="textSecondary">
              No data available.
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 0}
              sx={{
                color: '#6b7280',
                borderRadius: '0.5rem',
                border: '1px solid #e5e7eb',
                padding: '0.5rem 1rem',
                margin: '0 0.5rem',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                '&:disabled': {
                  color: '#9ca3af',
                  borderColor: '#e5e7eb',
                },
              }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: '#6b7280', margin: '0 0.5rem' }}>
              Page {page + 1} of {totalPages}
            </Typography>
            <Button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages - 1}
              sx={{
                color: '#6b7280',
                borderRadius: '0.5rem',
                border: '1px solid #e5e7eb',
                padding: '0.5rem 1rem',
                margin: '0 0.5rem',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                '&:disabled': {
                  color: '#9ca3af',
                  borderColor: '#e5e7eb',
                },
              }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SummaryReport;